import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { paths } from '@/app/paths';
import { useSession } from '@/app/session';
import { Button, Drawer, FieldHint, Input, KeyValueList, Pill, Segmented, Textarea, useToast, type Tone } from '@/design-system';
import { now } from '@/lib/clock';
import { formatDateShort, formatSum, formatTime } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { domainOrderKey, domainOrderQuery, domainsKey, OPERATOR_WAITING, operatorQueueKey, operatorQueueQuery, ordersKey, recordOperatorResult, type DomainOrder, type OperatorResult } from './api';
import s from './operator.module.css';

type Tv = (k: string, o?: Record<string, unknown>) => string;

/** The status pill: waiting for the operator (warn), a person must check (err), then the outcome. */
const TONE: Record<DomainOrder['status'], Tone> = {
  confirmed: 'mute',
  submitting: 'mute',
  submitted: 'mute',
  registry_pending: 'mute',
  operator_pending: 'warn',
  action_required: 'err',
  registered: 'ok',
  failed: 'err',
};

/** «2026-12-13» plus `years` (29 February becomes 28 February). */
function addYears(iso: string, years: number): string {
  const y = Number(iso.slice(0, 4)) + years;
  const md = iso.slice(5) === '02-29' ? '02-28' : iso.slice(5);
  return `${y}-${md}`;
}

/** The expiry an operator most likely records: a year (or the order's years) after today, or after the current expiry. */
function suggestedExpiry(o: DomainOrder): string {
  const from = o.kind === 'renew' && o.expires_on ? o.expires_on : now().toISOString().slice(0, 10);
  return addYears(from, o.years);
}

/**
 * «Заказы оператора» (P7-B02, additive; the settings drawers' pattern): the orders that wait for a person — the
 * assisted ahost.uz path, where an operator registers or renews in the registrar's panel, and orders nobody could
 * confirm (`action_required`). The list is `GET /admin/domain-orders`; an order opens its details and, for an
 * Admin (`domain:operate`), the result form (`POST /admin/domain-orders/{id}/operator-result`). A PM reads it.
 * Opened with `orderId` (from the production card), it shows that order and closes after the result.
 */
export function OperatorDrawer({ orderId, onClose }: { orderId?: string | null; onClose: () => void }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const { can } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(orderId ?? null);
  const [problem, setProblem] = useState<string | null>(null);
  const queue = useQuery(operatorQueueQuery);
  const order = useQuery({
    ...domainOrderQuery(selected ?? ''),
    enabled: !!selected,
    placeholderData: () => queue.data?.find((o) => o.id === selected),
  }).data;
  const operator = can('domain:operate');
  const waiting = !!order && OPERATOR_WAITING.includes(order.status);
  const back = () => {
    setProblem(null);
    if (orderId) onClose();
    else setSelected(null);
  };

  const save = useMutation({
    mutationFn: (body: OperatorResult) => recordOperatorResult(order?.id ?? '', body),
    onSuccess: (next) => {
      qc.setQueryData(domainOrderKey(next.id), next);
      void qc.invalidateQueries({ queryKey: operatorQueueKey });
      void qc.invalidateQueries({ queryKey: ordersKey(next.website.id) });
      void qc.invalidateQueries({ queryKey: domainsKey(next.website.id) });
      toast(next.status === 'registered' ? t('operator.done_registered', { domain: next.domain }) : t('operator.done_refused', { domain: next.domain }));
      back();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });

  const footer =
    order && waiting && operator ? (
      <div className={s.actions}>
        <Button size={38} variant="secondary" onClick={back}>
          {tc('actions.cancel')}
        </Button>
        <Button size={38} type="submit" form="operator-result" disabled={save.isPending}>
          {t('operator.submit')}
        </Button>
      </div>
    ) : undefined;
  const note = problem ? (
    <span className={s.problem} role="alert">
      {problem}
    </span>
  ) : order && waiting && !operator ? (
    t('operator.read_only')
  ) : undefined;

  return (
    <Drawer open onClose={onClose} title={t('operator.title')} sub={t('operator.sub')} closeLabel={tc('actions.close_esc')} footerNote={note} footer={footer}>
      {selected ? (
        order ? (
          <div className={s.body}>
            {!orderId && (
              <button type="button" className={s.backLink} onClick={back}>
                {t('operator.back')}
              </button>
            )}
            <OrderDetails order={order} />
            {waiting && operator && <ResultForm key={order.id} order={order} onProblem={setProblem} onSubmit={(body) => save.mutate(body)} />}
          </div>
        ) : null
      ) : (
        <Queue orders={queue.data} failed={queue.isError} onOpen={setSelected} />
      )}
    </Drawer>
  );
}

function Queue({ orders, failed, onOpen }: { orders: DomainOrder[] | undefined; failed: boolean; onOpen: (id: string) => void }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  if (failed) return <span className={s.empty}>{tc('errors.unexpected_response')}</span>;
  if (!orders) return null;
  if (!orders.length) return <span className={s.empty}>{t('operator.empty')}</span>;
  return (
    <ul className={s.list} aria-label={t('operator.title')}>
      {orders.map((o) => (
        <li key={o.id}>
          <button type="button" className={s.row} onClick={() => onOpen(o.id)} aria-label={t('operator.open_order', { domain: o.domain })}>
            <span className={s.rowTop}>
              <span className={s.domain}>{o.domain}</span>
              <Pill tone={TONE[o.status]} size="tagTight">
                {t(`order.status.${o.status}`)}
              </Pill>
            </span>
            <span className={s.rowSub}>{t('operator.row', { kind: t(`operator.kind.${o.kind}`), years: t('operator.years', { count: o.years }), amount: formatSum(o.amount, true) })}</span>
            <span className={s.rowSub}>{t('operator.client_value', { brand: o.organization.brand_name, site: o.website.name })}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function OrderDetails({ order: o }: { order: DomainOrder }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const { t: ts } = useTranslation('settings');
  const tv = tc as unknown as Tv;
  const confirmed = o.confirmed_by ? t('operator.confirmed_value', { name: o.confirmed_by.name, date: formatDateShort(o.confirmed_at), time: formatTime(o.confirmed_at) }) : tc('dash');
  const rows = [
    {
      key: 'status',
      label: t('operator.status'),
      value: (
        <Pill tone={TONE[o.status]} size="tagTight">
          {t(`order.status.${o.status}`)}
        </Pill>
      ),
    },
    { key: 'domain', label: t('buy.domain'), value: <span className={s.mono}>{o.domain}</span> },
    { key: 'operation', label: t('operator.operation'), value: t('operator.operation_value', { kind: t(`operator.kind.${o.kind}`), years: t('operator.years', { count: o.years }) }) },
    { key: 'registrar', label: t('operator.registrar'), value: (ts as unknown as Tv)(`domains.registrars.${o.registrar}`, { defaultValue: o.registrar }) },
    { key: 'amount', label: t('operator.amount'), value: t('operator.amount_value', { amount: formatSum(o.amount, true), date: formatDateShort(o.price_as_of) }) },
    {
      key: 'client',
      label: t('operator.client'),
      value: (
        <Link className={s.link} to={paths.site(o.website.id, 'domains')}>
          {t('operator.client_value', { brand: o.organization.brand_name, site: o.website.name })}
        </Link>
      ),
    },
    { key: 'owner', label: t('operator.owner'), value: o.owner ? t('operator.owner_value', { name: o.owner.name ?? tc('dash'), kind: t(`operator.owner_kind.${o.owner.kind}`) }) : tc('dash') },
    { key: 'ns', label: t('operator.nameservers'), value: o.nameservers.length ? <span className={s.mono}>{o.nameservers.join(' · ')}</span> : tc('dash') },
    { key: 'confirmed', label: t('operator.confirmed'), value: confirmed },
    ...(o.kind === 'renew' && o.expires_on && OPERATOR_WAITING.includes(o.status) ? [{ key: 'expires', label: t('operator.current_expiry'), value: formatDateShort(o.expires_on) }] : []),
    ...(o.error_code ? [{ key: 'error', label: t('operator.reason'), value: tv(`errors.${o.error_code}`, { defaultValue: o.error_code }) }] : []),
    ...(o.status === 'registered' && o.expires_on ? [{ key: 'until', label: t('operator.expires_on'), value: formatDateShort(o.expires_on) }] : []),
    ...(o.provider_ref ? [{ key: 'ref', label: t('operator.provider_ref'), value: <span className={s.mono}>{o.provider_ref}</span> }] : []),
  ];
  return <KeyValueList rows={rows} />;
}

function ResultForm({ order, onSubmit, onProblem }: { order: DomainOrder; onSubmit: (body: OperatorResult) => void; onProblem: (p: string | null) => void }) {
  const { t } = useTranslation('domains');
  const [outcome, setOutcome] = useState<OperatorResult['outcome']>('registered');
  const [expires, setExpires] = useState(() => suggestedExpiry(order));
  const [ref, setRef] = useState(order.provider_ref ?? '');
  const [note, setNote] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onProblem(null);
    if (outcome === 'registered' && !/^\d{4}-\d{2}-\d{2}$/.test(expires)) return onProblem(t('operator.expires_required'));
    onSubmit({
      outcome,
      expires_on: outcome === 'registered' ? expires : null,
      provider_ref: outcome === 'registered' ? ref.trim() || null : null,
      note: note.trim() || null,
    });
  };
  return (
    <form id="operator-result" className={s.form} onSubmit={submit} noValidate>
      <span className={s.groupLabel}>{t('operator.outcome')}</span>
      <Segmented
        variant="form"
        label={t('operator.outcome')}
        value={outcome}
        onChange={setOutcome}
        className={s.alignStart}
        options={[
          { value: 'registered', label: t(order.kind === 'renew' ? 'operator.outcome_renewed' : 'operator.outcome_registered') },
          { value: 'refused', label: t('operator.outcome_refused') },
        ]}
      />
      {outcome === 'registered' ? (
        <>
          <div className={s.grid2}>
            <Input label={t('operator.expires_on')} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
            <Input label={t('operator.provider_ref')} value={ref} onChange={(e) => setRef(e.target.value)} mono maxLength={120} autoComplete="off" />
          </div>
          <FieldHint>{t(order.kind === 'renew' ? 'operator.renewed_hint' : 'operator.registered_hint')}</FieldHint>
        </>
      ) : (
        <FieldHint>{t('operator.refused_hint')}</FieldHint>
      )}
      <Textarea label={t('operator.note')} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} />
      <FieldHint>{t('operator.note_hint')}</FieldHint>
    </form>
  );
}
